/* FLRC — reviews sections (homepage). Progressive: both sections work
   without this file (the rail scrolls natively, the statements stack).

   - [data-flrc-rv-cycle]  option C: one review at a time on a measuring
     scale; the cursor travels the scale and advances the review.
   - [data-flrc-rv-rail]   option B: position counter + prev/next buttons
     for the scroll-snap rail. */
(() => {
  const pad = (n) => String(n).padStart(2, '0');

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Option C: one review at a time on a measuring scale. The cursor's
  // position is (review + progress) / count; progress runs 0 -> 1 over the
  // interval while the section is on screen and not held by keyboard
  // focus, and arriving at the next tick advances the review. Words rise
  // through their slots in CSS; this only moves the classes.
  const initCycle = (root) => {
    if (root.dataset.flrcRvInit) return;
    root.dataset.flrcRvInit = '1';
    const quotes = [...root.querySelectorAll('[data-flrc-rv-quote]')];
    const ticks = [...root.querySelectorAll('[data-flrc-rv-tab]')];
    const count = quotes.length;
    if (!count) return;
    const secs = parseFloat(getComputedStyle(root).getPropertyValue('--flrc-rv-interval'));
    const interval = (secs > 0 ? secs : 8) * 1000;

    let current = 0;
    let progress = 0;
    let inview = false;
    let held = false;
    let paused = false;
    let raf = 0;
    let last = 0;
    const leaving = new Map();

    const place = () => root.style.setProperty('--flrc-rv-pos', ((current + progress) / count).toFixed(4));

    const show = (n) => {
      const next = (n + count) % count;
      quotes[next].classList.toggle('is-after', next !== current);
      if (next !== current) {
        const out = quotes[current];
        out.classList.remove('is-active');
        out.classList.add('is-leaving');
        clearTimeout(leaving.get(out));
        leaving.set(out, setTimeout(() => out.classList.remove('is-leaving'), 900));
      }
      current = next;
      progress = 0;
      quotes.forEach((q, i) => {
        const on = i === current;
        if (on) {
          clearTimeout(leaving.get(q));
          q.classList.remove('is-leaving');
        }
        q.classList.toggle('is-active', on);
        q.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      ticks.forEach((t, i) => {
        const on = i === current;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      place();
    };

    const frame = (now) => {
      const dt = last ? now - last : 0;
      last = now;
      if (!held && !paused) {
        progress += dt / interval;
        if (progress >= 1) show(current + 1);
        else place();
      }
      raf = requestAnimationFrame(frame);
    };
    const run = () => {
      const go = inview && !reduced() && count > 1;
      if (go && !raf) { last = 0; raf = requestAnimationFrame(frame); }
      if (!go && raf) { cancelAnimationFrame(raf); raf = 0; }
    };

    ticks.forEach((t, i) => {
      t.addEventListener('click', () => show(i));
      t.addEventListener('keydown', (e) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        show(current + step);
        ticks[current].focus();
      });
    });
    // The cursor never stops for a pointer: hover or a tap (which on a
    // phone leaves a "hover" behind and never ends it) would freeze it. A
    // press jumps to the start of that review and carries on at the same
    // speed. Only keyboard focus holds it, so a keyboard user can read.
    root.addEventListener('focusin', (e) => {
      held = e.target.matches(':focus-visible');
    });
    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) { held = false; last = 0; }
    });

    // Pause / play (WCAG 2.2.2). A press on the scale still jumps while
    // paused; it just doesn't start the cursor again.
    const pauseBtn = root.querySelector('[data-flrc-rv-pause]');
    if (pauseBtn && count > 1 && !reduced()) {
      const pauseLabel = pauseBtn.querySelector('[data-flrc-rv-pause-label]');
      pauseBtn.hidden = false;
      pauseBtn.addEventListener('click', () => {
        paused = !paused;
        last = 0;
        pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        if (pauseLabel) pauseLabel.textContent = paused ? 'Play' : 'Pause';
      });
    }

    const setInview = (on) => {
      inview = on;
      // First sight: let the opening review rise into place.
      if (on && !root.classList.contains('is-live')) requestAnimationFrame(() => root.classList.add('is-live'));
      run();
    };
    // In view = 35% of the section OR half the screen is covered by it. A
    // ratio alone is a height trap: on a landscape phone the section is taller
    // than a screen / 0.35, can never reach 35%, never goes live, and every
    // word stays parked below its slot — an empty quote area. Several
    // thresholds so the callback re-checks as it scrolls in.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) =>
          entries.forEach((e) =>
            setInview(
              e.isIntersecting &&
                (e.intersectionRatio >= 0.35 || e.intersectionRect.height >= window.innerHeight * 0.5)
            )
          ),
        { threshold: [0, 0.1, 0.2, 0.3, 0.35, 0.5] }
      ).observe(root);
    } else {
      setInview(true);
    }

    root.classList.add('is-ready');
    show(0);
  };

  const initRail = (root) => {
    if (root.dataset.flrcRvInit) return;
    root.dataset.flrcRvInit = '1';
    const track = root.querySelector('[data-flrc-rv-track]');
    const pos = root.querySelector('[data-flrc-rv-pos]');
    const prev = root.querySelector('[data-flrc-rv-prev]');
    const next = root.querySelector('[data-flrc-rv-next]');
    if (!track || !track.children.length) return;

    const step = () => {
      const a = track.children[0].getBoundingClientRect();
      const b = track.children[1] ? track.children[1].getBoundingClientRect() : null;
      return b ? b.left - a.left : a.width;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const atEnd = track.scrollLeft >= max - 4;
      const n = atEnd ? track.children.length : Math.round(track.scrollLeft / step()) + 1;
      if (pos) pos.textContent = pad(n);
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = atEnd;
    };
    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    }, { passive: true });
    const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: smooth }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: smooth }));
    window.addEventListener('resize', update);
    update();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-flrc-rv-cycle]').forEach(initCycle);
    scope.querySelectorAll('[data-flrc-rv-rail]').forEach(initRail);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
